import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Download } from 'lucide-react'

import AppFilter from './filters/AppFilter'
import { dateFilterLabel } from './filters/dateRange'
import DateRangeFilter from './filters/DateRangeFilter'
import { filtersToParams, todayIso } from './filters/params'
import TeamFilter from './filters/TeamFilter'
import { DEFAULT_FILTERS, type UsageFiltersState } from './filters/types'
import UserFilter from './filters/UserFilter'

import { isApiError } from '@/api'
import { exportWordsDictatedCsv, type WordsDictatedExportColumn } from '@/api/teamInsights'
import { useToast } from '@/components/Toast/ToastContext'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { downloadBlob } from '@/utils/csv'

interface ExportDialogProps {
  enterpriseId: string | null
  // Defaults for filters when the dialog opens — the page's current filters.
  initialFilters: UsageFiltersState
  disabled?: boolean
  // Fires after the CSV has actually downloaded — analytics callers attach here.
  onExport?: (columns: WordsDictatedExportColumn[]) => void
}

// Fixed display order from the design (Figma 1667:4521). Keep aligned with
// backend ``_EXPORT_COLUMN_HEADERS`` — the server reorders to this anyway,
// but we lock the UI so the column count under the button stays predictable.
const COLUMN_OPTIONS: { key: WordsDictatedExportColumn; label: string }[] = [
  { key: 'user', label: 'User' },
  { key: 'email', label: 'User email' },
  { key: 'team', label: 'Department' },
  { key: 'app_type', label: 'App type' },
  { key: 'app', label: 'Apps' },
]

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const buildExportFilename = (filters: UsageFiltersState) => {
  const parts: string[] = ['wispr-flow-usage']
  // Left-to-right filter order in the dialog: date, app, user, team.
  if (filters.date.kind !== 'preset' || filters.date.preset !== 'all') {
    parts.push(slugify(dateFilterLabel(filters.date)))
  }
  const appCount = filters.appKeys.length + filters.appCategories.length
  if (appCount === 1) {
    parts.push(slugify(filters.appKeys[0] ?? filters.appCategories[0] ?? 'app'))
  } else if (appCount > 1) {
    parts.push(`${appCount}-apps`)
  }
  if (filters.userIds.length === 1) parts.push('user')
  else if (filters.userIds.length > 1) parts.push(`${filters.userIds.length}-users`)
  if (filters.teamId) parts.push('team')
  parts.push(todayIso())
  return `${parts.join('-')}.csv`
}

// Each filter pill stretches to fill its grid column (per Figma 1667:4521 row).
const FILTER_TRIGGER_CLASS = 'w-full justify-start font-medium'

const ERROR_TOAST_MS = 6000

const ExportDialog = ({ enterpriseId, initialFilters, disabled, onExport }: ExportDialogProps) => {
  const [open, setOpen] = useState(false)
  const [filters, setFilters] = useState<UsageFiltersState>(initialFilters)
  // Column selection is intentionally sticky across opens — once a user tunes
  // which columns they want in their CSV, that choice should survive subsequent
  // exports. Filters re-seed from the page on open (see effect below).
  const [columns, setColumns] = useState<Set<WordsDictatedExportColumn>>(
    () => new Set(COLUMN_OPTIONS.map((c) => c.key))
  )
  const { updateToast } = useToast()

  // Re-seed the dialog from the page's filters every time it opens so the user
  // doesn't get stale state if they tweaked filters between exports.
  useEffect(() => {
    if (open) setFilters(initialFilters)
  }, [open, initialFilters])

  const toggleColumn = (key: WordsDictatedExportColumn) =>
    setColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const exportMutation = useMutation({
    mutationFn: async (selected: WordsDictatedExportColumn[]) => {
      if (!enterpriseId) throw new Error('Missing enterprise')
      const response = await exportWordsDictatedCsv(enterpriseId, filtersToParams(filters), selected)
      return { blob: response.data, selected }
    },
    onSuccess: ({ blob, selected }) => {
      downloadBlob(blob, buildExportFilename(filters))
      onExport?.(selected)
      setOpen(false)
    },
    onError: (error: unknown) => {
      if (isApiError(error) && error.status === 413) {
        updateToast(
          'This export is too large. Please narrow your date range or filters and try again.',
          'error',
          ERROR_TOAST_MS
        )
      } else {
        updateToast('Something went wrong exporting your data. Please try again.', 'error', ERROR_TOAST_MS)
      }
    },
  })

  const handleExport = () => {
    if (!enterpriseId || exportMutation.isPending) return
    const selected = COLUMN_OPTIONS.map((c) => c.key).filter((k) => columns.has(k))
    exportMutation.mutate(selected)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (disabled && next) return
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          disabled={disabled}
          className="size-10 shrink-0 px-0"
          aria-label="Export usage data"
          title="Export usage data"
        >
          <Download className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-6 font-[Figtree] sm:max-w-[696px]">
        <DialogHeader>
          <DialogTitle className="text-foreground text-[18px] leading-7 font-[550] whitespace-nowrap">
            Export usage data
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-1">
          <div className="text-foreground flex items-center justify-between gap-2 rounded-md p-1 text-[15px] leading-5">
            <span className="min-w-0 flex-1 font-normal">Filters</span>
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="min-w-0 flex-1 text-right font-[550] hover:underline"
            >
              Clear filters
            </button>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <DateRangeFilter
              value={filters.date}
              onChange={(date) => setFilters({ ...filters, date })}
              triggerClassName={FILTER_TRIGGER_CLASS}
            />
            <AppFilter
              enterpriseId={enterpriseId}
              appKeys={filters.appKeys}
              appCategories={filters.appCategories}
              onChange={({ appKeys, appCategories }) => setFilters({ ...filters, appKeys, appCategories })}
              triggerClassName={FILTER_TRIGGER_CLASS}
            />
            <UserFilter
              enterpriseId={enterpriseId}
              value={filters.userIds}
              onChange={(userIds) => setFilters({ ...filters, userIds })}
              triggerClassName={FILTER_TRIGGER_CLASS}
            />
            <TeamFilter
              value={filters.teamId}
              onChange={(teamId) => setFilters({ ...filters, teamId })}
              triggerClassName={FILTER_TRIGGER_CLASS}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-foreground rounded-md p-1 text-[15px] leading-5 font-normal">Columns</div>
          <div className="border-input bg-card flex flex-col gap-3 rounded-lg border px-4 py-3">
            {COLUMN_OPTIONS.map(({ key, label }) => (
              <label key={key} className="flex cursor-pointer items-center gap-2 text-[15px] leading-5 font-medium">
                <Checkbox checked={columns.has(key)} onCheckedChange={() => toggleColumn(key)} aria-label={label} />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleExport} disabled={exportMutation.isPending || !enterpriseId || columns.size === 0}>
            {exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ExportDialog
